import type Bluebird from 'bluebird';
import type { PidlLogger } from '../types';

type RejectionState = { isRejected(): boolean };

export default class {
  pidlLogger?: PidlLogger | null;
  constructor(pidlLogger?: PidlLogger | null) {
    this.pidlLogger = pidlLogger;
  }

  private getActionLogger(action: string): import('../types').PidlLoggerAction | undefined {
    return (this.pidlLogger as (PidlLogger & Record<string, import('../types').PidlLoggerAction>) | null | undefined)?.[action];
  }


  authFail = (error: unknown, authenticationPromise: RejectionState, pidlInfoPromise?: RejectionState) => {
    if (this.pidlLogger) {
      let errorSource;
      if (authenticationPromise.isRejected()) {
        errorSource = this.pidlLogger.auth.getAction();
      } else if (pidlInfoPromise?.isRejected()) {
        errorSource = this.pidlLogger.getPidl.getAction();
      } else {
        errorSource = 'Promise.all';
      }
      this.pidlLogger.fail('PidlTerminatingError', errorSource, error);
    }
    return this;
  };


  pidlFail = (error: unknown, debugInfo: unknown) => {
    if (this.pidlLogger) {
      this.pidlLogger.pidlFail(error, debugInfo);
    }
    return this;
  };


  setOption = (key: string, value: unknown) => {
    if (this.pidlLogger) {
      this.pidlLogger.setOption(key, value);
    }
    return this;
  };


  setOptions = (dictionary: unknown) => {
    if (this.pidlLogger) {
      this.pidlLogger.setOptions(dictionary);
    }
    return this;
  };


  setOptionsAction = (action: string, dictionary: unknown) => {
    const actionLogger = this.getActionLogger(action);
    if (actionLogger) {
      actionLogger.setOptions(dictionary);
    }
    return this;
  };


  setPidlOption = (key: string, value: unknown) => {
    if (this.pidlLogger) {
      this.pidlLogger.setPidlOption(key, value);
    }
    return this;
  };


  addTaps = <T>(action: string, promise: Bluebird<T>): Bluebird<T> => {
    const actionLogger = this.getActionLogger(action);
    if (actionLogger) {
      return actionLogger.addTaps(promise);
    }
    return promise;
  };


  start = (action?: string) => {
    if (this.pidlLogger) {
      if (action) {
        // if action is passed in, we want don't want to start the whole logger,
        // even if the action doesn't actually exist
        const actionLogger = this.getActionLogger(action);
        if (actionLogger) {
          actionLogger.start();
        }
      } else {
        this.pidlLogger.start();
      }
    }
    return this;
  };


  succeed = (action?: string) => {
    if (this.pidlLogger) {
      if (action) {
        // if action is passed in, we want don't want to suceed the whole logger,
        // even if the action doesn't actually exist
        const actionLogger = this.getActionLogger(action);
        if (actionLogger) {
          actionLogger.succeed();
        }
      } else {
        this.pidlLogger.succeed();
      }
    }
    return this;
  };


  fail = (error: unknown) => {
    if (this.pidlLogger) {
      this.pidlLogger.fail(error);
    }
    return this;
  };


  validationFail = (action: string, error?: unknown) => {
    const actionLogger = this.getActionLogger(action);
    if (actionLogger) {
      actionLogger.validationFail(error);
    }
    return this;
  };


  userFail = (reason: string, additionalDetails?: unknown) => {
    if (this.pidlLogger) {
      this.pidlLogger.userFail(reason, additionalDetails);
    }
    return this;
  };

  clearOptions = () => {
    if (this.pidlLogger) {
      this.pidlLogger.clearOptions();
    }
    return this;
  };
}
